import { type PropsWithChildren, useState } from "react";
import { type ConnectionStateChange, Realtime } from "ably";
import { AblyProvider, ChannelProvider } from "ably/react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { toast } from "sonner";
import { z } from "zod";
import { FIC_CHECKLIST_WEB_FEATURE_FLAG, LIVE_CALL_WIDGET_FEATURE_FLAG } from "@remi/constants";
import { isLocalDevelopmentEnvironment } from "@remi/environment";
import { getError } from "@remi/error";
import { remiLogger } from "@remi/logging";
import { useEffect } from "@remi/next-ui";
import { CacheMessageType, suggestedActionChannelName } from "@remi/types/socket/message";

import { getClerkStepBucket } from "~/utils/ablyAuthTiming";
import { isAblyConnectionStateActive } from "~/utils/ablyConnectionState";
import { isAblyNetworkUnreachableError, isAblyRelatedError, isSuppressibleAblyError } from "~/utils/ablyErrors";
import { api, getClerkToken, proxyApi } from "~/utils/api";
import { clearAllCache } from "~/utils/cache";
import Loading from "~/components/Loading";
import { NotificationToast } from "~/components/notifications/toast/NotificationToast";
import { NotificationToaster } from "~/components/notifications/toast/NotificationToaster";
import { SuggestedActionWidget } from "~/components/suggested-action/SuggestedActionWidget";
import { useCacheChannel } from "~/contexts/hooks/useCacheChannel";
import { useNotificationChannel } from "~/contexts/hooks/useNotificationChannel";
import { useSuggestedActionChannel } from "~/contexts/hooks/useSuggestedActionChannel";
import { PresenceProvider } from "~/contexts/PresenceContext";
import { SuggestedActionProvider, useSuggestedAction } from "~/contexts/SuggestedActionContext";
import { handleAblyIdentityDivergence } from "~/contexts/utils/ablyIdentity";
import { useUserProfile } from "./UserProfileContext";

const DEV_ABLY_REALTIME_REQUEST_TIMEOUT_MS = 30_000;
// Ably's default realtimeRequestTimeout
const ABLY_REALTIME_REQUEST_TIMEOUT_MS = 10_000;

// ably.d.ts declares Connection.errorReason non-nullable, but the implementation assigns null to it
// (connection.ts:25, connectionmanager.ts:720), so tsc cannot catch a bare read.
const AblyErrorReasonSchema = z.object({
  code: z.number(),
  statusCode: z.number(),
  message: z.string(),
});

const swallowAblyErrorEvent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

export const AblyWrapper = ({ children }: PropsWithChildren) => {
  const { user } = useUserProfile();
  const utils = api.useUtils();
  const [realtime, setRealtime] = useState<Realtime | null>(null);
  useEffect(() => {
    const isLocalDev = isLocalDevelopmentEnvironment();
    // Closure-local, not a ref: it must reset when this client is rebuilt, so a second
    // impersonation transition in the same session is still repaired.
    let identityMismatchHandled = false;
    let inFlightAuthCallbacks = 0;
    const realtimeRequestTimeout = isLocalDev ? DEV_ABLY_REALTIME_REQUEST_TIMEOUT_MS : ABLY_REALTIME_REQUEST_TIMEOUT_MS;
    const client = new Realtime({
      realtimeRequestTimeout,
      authCallback: async (_tokenParams, callback) => {
        const startAttributes = {
          connectionState: client.connection.state,
          visibilityAtStart: document.visibilityState,
          onlineAtStart: String(navigator.onLine),
          overlapping: String(inFlightAuthCallbacks > 0),
        };
        inFlightAuthCallbacks += 1;
        const startTime = Date.now();
        let clerkDuration: number | null = null;
        const timeoutTimer = setTimeout(() => {
          remiLogger.captureCounterMetric("ably.auth.token-request-timeout", 1, {
            attributes: {
              ...startAttributes,
              stage: clerkDuration === null ? "clerk" : "request",
              visibilityAtTimeout: document.visibilityState,
            },
          });
        }, realtimeRequestTimeout);

        try {
          const clerkToken = await getClerkToken();
          clerkDuration = Date.now() - startTime;
          remiLogger.captureDistributionMetric("ably.auth.clerk-token-duration", clerkDuration, {
            attributes: {
              ...startAttributes,
              visibilityAtEnd: document.visibilityState,
              hasToken: String(clerkToken !== null),
            },
          });

          const token = await proxyApi.external.getAblyAuth.query();
          const duration = Date.now() - startTime;

          remiLogger.captureDistributionMetric("ably.auth.token-request-duration", duration, {
            attributes: {
              ...startAttributes,
              visibilityAtEnd: document.visibilityState,
              clerkStep: getClerkStepBucket(clerkDuration),
            },
          });
          remiLogger.captureCounterMetric("ably.auth.token-request-success");

          callback(null, token);

          if (!identityMismatchHandled) {
            identityMismatchHandled = handleAblyIdentityDivergence({
              cachedPersonId: user.personId,
              tokenClientId: token.clientId,
              cachedIdentityWasImposter: !!user.imposterId,
              refreshIdentity: async () => {
                await utils.auth.me.invalidate();
                await Promise.all([
                  utils.communication.searchNotifications.invalidate(),
                  utils.communication.searchNotificationsCount.invalidate(),
                ]);
              },
            });
          }
          return;
        } catch (error) {
          const duration = Date.now() - startTime;
          const failureAttributes = {
            ...startAttributes,
            visibilityAtEnd: document.visibilityState,
            status: "failure",
          };

          if (clerkDuration === null) {
            remiLogger.captureDistributionMetric("ably.auth.clerk-token-duration", duration, {
              attributes: failureAttributes,
            });
          }
          remiLogger.captureDistributionMetric("ably.auth.token-request-duration", duration, {
            attributes: {
              ...failureAttributes,
              clerkStep: clerkDuration === null ? "failed" : getClerkStepBucket(clerkDuration),
            },
          });
          remiLogger.captureCounterMetric("ably.auth.token-request-failure");

          remiLogger.debug("Ably auth token request failed", {
            error: getError(error).message,
            duration,
          });

          callback(getError(error).message, null);
          return;
        } finally {
          clearTimeout(timeoutTimer);
          inFlightAuthCallbacks -= 1;
        }
      },
      logLevel: isLocalDev ? 0 : 1,
    });

    const failedOrSuspendedHandler = (stateChange: ConnectionStateChange) => {
      const parsedReason = AblyErrorReasonSchema.safeParse(client.connection.errorReason as unknown);
      const errorReason = parsedReason.success ? parsedReason.data : null;
      const state = stateChange.current;
      const code = String(errorReason?.code ?? "unknown");
      const statusCode = String(errorReason?.statusCode ?? "unknown");
      const message = errorReason?.message;

      if (state === "failed") {
        remiLogger.captureCounterMetric("ably.connection.failed", 1, {
          attributes: {
            previousState: stateChange.previous,
            code,
            statusCode,
            message,
          },
        });
      } else if (state === "suspended") {
        remiLogger.captureCounterMetric("ably.connection.suspended", 1, {
          attributes: {
            previousState: stateChange.previous,
            code,
            statusCode,
            message,
          },
        });
      }

      if (isAblyNetworkUnreachableError(errorReason)) {
        remiLogger.captureCounterMetric("ably.connection.network-unreachable", 1, {
          attributes: {
            state,
            previousState: stateChange.previous,
            code,
            statusCode,
          },
        });
      }
    };

    // Handle connection errors and log as metrics to prevent unhandled rejections
    // Listen to "failed" and "suspended" states which emit error events
    client.connection.on(["failed", "suspended"], failedOrSuspendedHandler);

    const suppressNoisyAblyError = (errorLike: unknown) => {
      if (!isSuppressibleAblyError(errorLike)) {
        return false;
      }
      return true;
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (suppressNoisyAblyError(event.reason)) {
        swallowAblyErrorEvent(event);
        return;
      }

      if (!isLocalDev || !isAblyRelatedError(event.reason)) {
        return;
      }
      swallowAblyErrorEvent(event);
    };

    const handleWindowError = (event: ErrorEvent) => {
      const errorLike = event.error ?? event.message ?? event.filename;
      if (suppressNoisyAblyError(errorLike)) {
        swallowAblyErrorEvent(event);
        return;
      }

      if (!isLocalDev || !isAblyRelatedError(errorLike)) {
        return;
      }
      swallowAblyErrorEvent(event);
    };

    // biome-ignore lint/suspicious/noConsole: dev-only filter to suppress noisy Ably console errors.
    const originalConsoleError = window.console.error;
    // biome-ignore lint/suspicious/noConsole: dev-only filter to suppress noisy Ably console errors.
    const originalConsoleWarn = window.console.warn;
    const shouldSuppressConsoleArgs = (...args: unknown[]) => {
      return args.some((arg) => isSuppressibleAblyError(arg) || isAblyRelatedError(arg));
    };

    const handleConsoleError = (...args: unknown[]) => {
      if (shouldSuppressConsoleArgs(...args)) {
        return;
      }
      originalConsoleError(...args);
    };

    const handleConsoleWarn = (...args: unknown[]) => {
      if (shouldSuppressConsoleArgs(...args)) {
        return;
      }
      originalConsoleWarn(...args);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("unhandledrejection", handleUnhandledRejection, { capture: true });
      window.addEventListener("error", handleWindowError, { capture: true });
    }

    if (isLocalDev && typeof window !== "undefined") {
      window.console.error = handleConsoleError;
      window.console.warn = handleConsoleWarn;
    }

    setRealtime(client);
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("unhandledrejection", handleUnhandledRejection, { capture: true });
        window.removeEventListener("error", handleWindowError, { capture: true });
      }

      if (isLocalDev && typeof window !== "undefined") {
        window.console.error = originalConsoleError;
        window.console.warn = originalConsoleWarn;
      }
      client.connection.off();
      if (isAblyConnectionStateActive(client.connection.state)) {
        client.close();
      }
    };
  }, [user.personId, user.imposterId, utils]);
  if (!realtime) {
    return <Loading />;
  }

  return (
    <AblyProvider client={realtime}>
      <PresenceProvider>
        <ChannelProvider channelName="cache-full:full">
          <FullCacheManager />
        </ChannelProvider>
        <LivePhotoReviewAblyChannel />
        <SuggestedActionAblyChannel personId={user.person.id} />
        <ChannelProvider channelName={`notifications-${user.personId}`}>
          <NotificationManager personId={user.person.id} />
          <NotificationToaster />
          {children}
        </ChannelProvider>
      </PresenceProvider>
    </AblyProvider>
  );
};

const LivePhotoReviewAblyChannel = () => {
  const enabled = useFeatureFlagEnabled(FIC_CHECKLIST_WEB_FEATURE_FLAG);
  if (!enabled) {
    return null;
  }
  return (
    <ChannelProvider channelName="cache-live_photo_review:queue">
      <LivePhotoReviewCacheManager />
    </ChannelProvider>
  );
};

const FullCacheManager = () => {
  useCacheChannel(
    {
      entityType: CacheMessageType.FULL,
      entityId: "full",
    },
    () => {
      void clearAllCache({
        clearQueryClient: true,
        clearIndexedDb: true,
        clearLocalStorage: false,
        clearSessionStorage: false,
      });
    },
  );
  return null;
};

const LivePhotoReviewCacheManager = () => {
  const utils = api.useUtils();
  useCacheChannel(
    {
      entityType: CacheMessageType.LIVE_PHOTO_REVIEW,
      entityId: "queue",
    },
    () => {
      void utils.livePhotoReview.queue.invalidate();
      void utils.livePhotoReview.queueCount.invalidate();
      void utils.livePhotoReview.submissionDetail.invalidate();
    },
  );
  return null;
};

const SuggestedActionManager = ({ personId }: { personId: string }) => {
  const { setAction } = useSuggestedAction();
  useSuggestedActionChannel(personId, setAction);
  return null;
};

const SuggestedActionAblyChannel = ({ personId }: { personId: string }) => {
  const enabled = useFeatureFlagEnabled(LIVE_CALL_WIDGET_FEATURE_FLAG);
  if (enabled !== true) {
    return null;
  }
  return (
    <SuggestedActionProvider>
      {/* rewind delivers an action published during the flag-load window, before this attaches */}
      <ChannelProvider channelName={suggestedActionChannelName(personId)} options={{ params: { rewind: "1" } }}>
        <SuggestedActionManager personId={personId} />
      </ChannelProvider>
      <SuggestedActionWidget />
    </SuggestedActionProvider>
  );
};

const NotificationManager = ({ personId }: { personId: string }) => {
  const utils = api.useUtils();
  useNotificationChannel(personId, (notification, eventId) => {
    void utils.communication.searchNotifications.invalidate();
    void utils.communication.searchNotificationsCount.invalidate();
    toast(<NotificationToast notification={notification} eventId={eventId} />, {
      id: notification.id,
      toasterId: "notifications",
      duration: Infinity,
      unstyled: true,
    });
  });
  return null;
};
